import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Plus, MoreVertical, Trash2, Edit, GripVertical, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '../api/client';

const KanbanBoard = ({ projectId, onProgressUpdate }) => {
  const [columns, setColumns] = useState([
    { id: 'todo', title: 'To Do', tasks: [] },
    { id: 'in-progress', title: 'In Progress', tasks: [] },
    { id: 'review', title: 'Review', tasks: [] },
    { id: 'done', title: 'Done', tasks: [] }
  ]);
  const [loading, setLoading] = useState(true);
  const [draggedTask, setDraggedTask] = useState(null);
  const [draggedFromColumn, setDraggedFromColumn] = useState(null);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [currentColumn, setCurrentColumn] = useState(null);
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    priority: 'medium',
    assigned_to: ''
  });
  const [saving, setSaving] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const { user } = useAuth();

  // assignment search state
  const [assignedQuery, setAssignedQuery] = useState('');
  const [assignedResults, setAssignedResults] = useState([]);
  const [assignedPage, setAssignedPage] = useState(1);
  const [assignedLoading, setAssignedLoading] = useState(false);
  const [assignedShow, setAssignedShow] = useState(false);

  const fetchProjectTeam = React.useCallback(async () => {
    try {
      const res = await apiClient.get(`/projects/${projectId}/team`);
      const data = res.data;
      const items = data && data.items ? data.items : (Array.isArray(data) ? data : []);
      setTeamMembers(items);
    } catch (err) {
      // ignore
    }
  }, [projectId]);

  const fetchAssignedMembers = React.useCallback(async (q = '', page = 1, append = false) => {
    try {
      setAssignedLoading(true);
      const res = await apiClient.get(`/projects/${projectId}/team`, { params: { q, page, per_page: 8 } }).catch(async () => {
        const r = await apiClient.get('/team', { params: { q, page, per_page: 8 } });
        return r;
      });
      const data = res.data;
      const items = data && data.items ? data.items : (Array.isArray(data) ? data : []);
      if (append) setAssignedResults(prev => [...prev, ...items]); else setAssignedResults(items);
      setAssignedPage(page);
    } catch (err) {
      // ignore
    } finally {
      setAssignedLoading(false);
    }
  }, [projectId]);

  const debouncedFetch = React.useMemo(
    () => require('lodash.debounce')((q) => fetchAssignedMembers(q, 1), 300),
    [fetchAssignedMembers]
  );

  useEffect(() => {
    if (projectId) {
      fetchProjectTeam();
      fetchAssignedMembers('', 1);
    }
  }, [projectId, fetchProjectTeam, fetchAssignedMembers]);

  const loadTasks = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/tasks/projects/${projectId}`);
      const tasks = response.data;

      // Organize tasks by column
      setColumns(prev => prev.map(col => ({
        ...col,
        tasks: tasks.filter(task => task.column_id === col.id)
      })));
    } catch (error) {
      console.error('Failed to load tasks:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const handleDragStart = (task, columnId) => {
    setDraggedTask(task);
    setDraggedFromColumn(columnId);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (targetColumnId) => {
    if (!draggedTask || !draggedFromColumn || draggedFromColumn === targetColumnId) {
      setDraggedTask(null);
      setDraggedFromColumn(null);
      return;
    }

    try {
      // Update task column in backend
      await apiClient.put(`/tasks/projects/${projectId}/${draggedTask.task_id}`, {
        column_id: targetColumnId
      });

      // Update local state
      const updatedColumns = columns.map(col => {
        if (col.id === draggedFromColumn) {
          return {
            ...col,
            tasks: col.tasks.filter(t => t.task_id !== draggedTask.task_id)
          };
        }
        if (col.id === targetColumnId) {
          return {
            ...col,
            tasks: [...col.tasks, { ...draggedTask, column_id: targetColumnId }]
          };
        }
        return col;
      });

      setColumns(updatedColumns);
      setDraggedTask(null);
      setDraggedFromColumn(null);
      toast.success('Task moved successfully');

      // Update project progress
      if (onProgressUpdate) {
        onProgressUpdate();
      }
    } catch (error) {
      console.error('Failed to move task:', error);
      toast.error('Failed to move task');
      setDraggedTask(null);
      setDraggedFromColumn(null);
    }
  };

  const openTaskModal = (columnId, task = null) => {
    setCurrentColumn(columnId);
    if (task) {
      setEditingTask(task);
      setTaskForm({
        title: task.title,
        description: task.description || '',
        priority: task.priority || 'medium',
        assigned_to: task.assigned_to || ''
      });
      if (task.assigned_to) {
        // ensure assigned member appears in results
        fetchAssignedMembers('', 1);
      }
    } else {
      setEditingTask(null);
      setTaskForm({ title: '', description: '', priority: 'medium', assigned_to: '' });
    }
    setTaskModalOpen(true);
  };

  const handleSaveTask = async () => {
    if (!taskForm.title.trim()) {
      toast.error('Task title is required');
      return;
    }

    try {
      setSaving(true);

      if (editingTask) {
        // Update existing task
        const response = await apiClient.put(
          `/tasks/projects/${projectId}/${editingTask.task_id}`,
          taskForm
        );

        const updatedColumns = columns.map(col => {
          if (col.id === currentColumn) {
            return {
              ...col,
              tasks: col.tasks.map(t =>
                t.task_id === editingTask.task_id ? response.data : t
              )
            };
          }
          return col;
        });

        setColumns(updatedColumns);
        toast.success('Task updated');
      } else {
        // Create new task
        const response = await apiClient.post(`/tasks/projects/${projectId}`, {
          ...taskForm,
          column_id: currentColumn
        });

        const updatedColumns = columns.map(col => {
          if (col.id === currentColumn) {
            return {
              ...col,
              tasks: [...col.tasks, response.data]
            };
          }
          return col;
        });

        setColumns(updatedColumns);
        toast.success('Task created');
      }

      setTaskModalOpen(false);

      // Update project progress
      if (onProgressUpdate) {
        onProgressUpdate();
      }
    } catch (error) {
      console.error('Failed to save task:', error);
      toast.error('Failed to save task');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTask = async (columnId, taskId) => {
    try {
      await apiClient.delete(`/tasks/projects/${projectId}/${taskId}`);

      const updatedColumns = columns.map(col => {
        if (col.id === columnId) {
          return {
            ...col,
            tasks: col.tasks.filter(t => t.task_id !== taskId)
          };
        }
        return col;
      });

      setColumns(updatedColumns);
      toast.success('Task deleted');

      // Update project progress
      if (onProgressUpdate) {
        onProgressUpdate();
      }
    } catch (error) {
      console.error('Failed to delete task:', error);
      toast.error('Failed to delete task');
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-blue-100 text-blue-800 border-blue-300',
      medium: 'bg-amber-100 text-amber-800 border-amber-300',
      high: 'bg-rose-100 text-rose-800 border-rose-300'
    };
    return colors[priority] || colors.medium;
  };

  const getColumnColor = (columnId) => {
    const colors = {
      'todo': 'border-slate-300 bg-slate-50',
      'in-progress': 'border-blue-300 bg-blue-50',
      'review': 'border-amber-300 bg-amber-50',
      'done': 'border-emerald-300 bg-emerald-50'
    };
    return colors[columnId] || 'border-slate-300 bg-slate-50';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <>
      <div className="flex overflow-x-auto pb-4 gap-6 lg:grid lg:grid-cols-4 lg:overflow-x-visible lg:pb-0">
        {columns.map((column) => (
          <div
            key={column.id}
            className="flex flex-col min-w-[280px] md:min-w-[320px] lg:min-w-0 flex-shrink-0 lg:flex-shrink"
            onDragOver={handleDragOver}
            onDrop={() => handleDrop(column.id)}
          >
            <Card className={`border-2 ${getColumnColor(column.id)} shadow-sm h-full`}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    {column.title}
                    <Badge variant="secondary" className="ml-2">
                      {column.tasks.length}
                    </Badge>
                  </CardTitle>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => openTaskModal(column.id)}
                    className="h-8 w-8 p-0"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3 min-h-[400px]">
                {column.tasks.map((task) => (
                  <div
                    key={task.task_id}
                    draggable
                    onDragStart={() => handleDragStart(task, column.id)}
                    className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm hover:shadow-md transition-all cursor-move group"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-start gap-2 flex-1">
                        <GripVertical className="h-4 w-4 text-slate-400 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                        <div className="flex-1">
                          <h4 className="font-medium text-slate-900">{task.title}</h4>
                          {task.assigned_to && (
                            <div className="text-xs text-slate-500 mt-1">Assigned to: { (teamMembers.find(tm => tm.member_id === task.assigned_to)?.name) || (user && task.assigned_to === user.user_id ? 'You' : task.assigned_to) }</div>
                          )}
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openTaskModal(column.id, task)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleDeleteTask(column.id, task.task_id)}
                            className="text-rose-600"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    {task.description && (
                      <p className="text-sm text-slate-600 mb-3 line-clamp-2">
                        {task.description}
                      </p>
                    )}
                    <div className="flex items-center justify-between">
                      <Badge className={`${getPriorityColor(task.priority)} border text-xs`}>
                        {task.priority}
                      </Badge>
                      <span className="text-xs text-slate-400">
                        {new Date(task.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
                {column.tasks.length === 0 && (
                  <div className="flex items-center justify-center h-32 text-slate-400 text-sm">
                    No tasks yet
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        ))}
      </div>

      <Dialog open={taskModalOpen} onOpenChange={setTaskModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingTask ? 'Edit Task' : 'Create New Task'}</DialogTitle>
            <DialogDescription>
              {editingTask ? 'Update task details below' : 'Add a new task to your board'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Task Title</label>
              <Input
                placeholder="Enter task title"
                value={taskForm.title}
                onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Description</label>
              <Textarea
                placeholder="Enter task description (optional)"
                value={taskForm.description}
                onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                rows={4}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Priority</label>
              <div className="flex gap-2">
                {['low', 'medium', 'high'].map((priority) => (
                  <Button
                    key={priority}
                    type="button"
                    variant={taskForm.priority === priority ? 'default' : 'outline'}
                    onClick={() => setTaskForm({ ...taskForm, priority })}
                    className={`flex-1 ${
                      taskForm.priority === priority
                        ? priority === 'low'
                          ? 'bg-blue-600 hover:bg-blue-700'
                          : priority === 'medium'
                          ? 'bg-amber-600 hover:bg-amber-700'
                          : 'bg-rose-600 hover:bg-rose-700'
                        : ''
                    }`}
                  >
                    {priority.charAt(0).toUpperCase() + priority.slice(1)}
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Assign To</label>
              <div className="relative">
                <input
                  className="border px-3 py-2 rounded w-full"
                  placeholder="Search members or type 'me'"
                  value={assignedShow ? assignedQuery : (taskForm.assigned_to === 'me' ? 'You' : (teamMembers.find(tm => tm.member_id === taskForm.assigned_to)?.name || ''))}
                  onChange={(e) => {
                    const q = e.target.value;
                    setAssignedQuery(q);
                    setAssignedShow(true);
                    if (taskForm.assigned_to) setTaskForm(prev => ({ ...prev, assigned_to: '' }));
                    debouncedFetch(q);
                  }}
                  onFocus={() => {
                    setAssignedShow(true);
                    if (taskForm.assigned_to) {
                        const currentName = taskForm.assigned_to === 'me' ? 'You' : (teamMembers.find(tm => tm.member_id === taskForm.assigned_to)?.name || '');
                        setAssignedQuery(currentName);
                    }
                    if (assignedResults.length === 0) fetchAssignedMembers('', 1);
                  }}
                />

                {assignedShow && (
                  <div className="absolute left-0 mt-1 w-full bg-white border rounded shadow z-50 max-h-60 overflow-auto">
                    <div className="p-2 cursor-pointer hover:bg-slate-50" onMouseDown={() => { setTaskForm({ ...taskForm, assigned_to: '' }); setAssignedShow(false); }}>Unassigned</div>
                    <div className="p-2 cursor-pointer hover:bg-slate-50" onMouseDown={() => { setTaskForm({ ...taskForm, assigned_to: 'me' }); setAssignedShow(false); }}>Assign to me</div>
                    {assignedResults.map(m => (
                      <div key={m.member_id} className="p-2 cursor-pointer hover:bg-slate-50" onMouseDown={() => { setTaskForm({ ...taskForm, assigned_to: m.member_id }); setAssignedShow(false); }}>
                        <div className="font-medium">{m.name}</div>
                        <div className="text-xs text-slate-400">{m.role || ''}</div>
                      </div>
                    ))}
                    {assignedLoading && <div className="p-2 text-center">Loading...</div>}
                    {!assignedLoading && assignedResults.length > 0 && (
                      <div className="p-2 text-center">
                        <button className="text-indigo-600" onMouseDown={() => fetchAssignedMembers(assignedQuery, assignedPage + 1, true)}>Load more</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTaskModalOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveTask} 
              className="bg-indigo-600 hover:bg-indigo-700"
              disabled={saving}
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                editingTask ? 'Update Task' : 'Create Task'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default KanbanBoard;
